import { Card } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ExternalLink, Key, Shield, AlertTriangle, CheckCircle, HelpCircle } from 'lucide-react';

const steps = [
  {
    num: 1,
    title: 'Create a DATEV Developer Account',
    desc: 'Visit the DATEV Developer Portal and register for a developer account. You will need a valid DATEV customer number.',
    link: 'https://developer.datev.de',
  },
  {
    num: 2,
    title: 'Register Your Application',
    desc: 'In the Developer Portal, create a new application. Select "Veridex Integration" as the application type. Choose the required API scopes for accounting data access.',
  },
  {
    num: 3,
    title: 'Obtain API Credentials',
    desc: 'After app registration, DATEV will provide your Client ID and Client Secret. Copy these carefully - the Client Secret is shown only once.',
  },
  {
    num: 4,
    title: 'Enter Credentials in Veridex',
    desc: 'Navigate to Settings > DATEV Integration in Veridex One. Paste your Client ID and Client Secret, then click "Save & Test".',
    link: '/settings/datev',
    linkInternal: true,
  },
  {
    num: 5,
    title: 'Verify Connection',
    desc: 'Click "Save & Test" to validate your credentials. A green "Connected" status confirms successful integration.',
  },
];

const faqs = [
  { q: 'What is the BYOL (Bring Your Own License) model?', a: 'You maintain your own DATEV subscription and API access. Veridex connects to your DATEV account using the credentials you provide. We do not charge additional fees for the DATEV integration itself.' },
  { q: 'Are my credentials stored securely?', a: 'Yes. Your Client Secret is encrypted using AES-based encryption before being stored in our database. We never store plain-text secrets and never transmit them to third parties.' },
  { q: 'What DATEV APIs does Veridex use?', a: 'Currently, we use the DATEV Accounting API to import energy invoices and supplier cost data for carbon footprint calculations. Future phases may include direct journal entry synchronization.' },
  { q: 'What if the connection test fails?', a: 'Ensure your Client ID is at least 8 characters and Client Secret is at least 12 characters. Verify that your DATEV developer account is active and the application has the correct API scopes enabled.' },
  { q: 'Can I disconnect DATEV at any time?', a: 'Yes. Simply click "Disconnect" on the DATEV Integration page. This removes your stored credentials from our system.' },
];

export default function DATEVHelp() {
  return (
    <div data-testid="datev-help-page" className="space-y-8 max-w-3xl mx-auto">
      <div className="cp-page-header">
        <h1 className="cp-page-title flex items-center gap-2">
          <HelpCircle className="w-6 h-6 text-accent" /> DATEV Integration Guide
        </h1>
        <Link to="/settings/datev">
          <Button variant="outline" size="sm" data-testid="datev-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Settings
          </Button>
        </Link>
      </div>

      {/* Overview */}
      <Card className="p-6 animate-fade-in" data-testid="datev-overview">
        <h2 className="text-lg font-heading font-bold mb-2">Overview</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Veridex integrates with DATEV to import financial and energy data directly into your
          carbon accounting workflow. This eliminates manual data entry and ensures your emission calculations
          are based on accurate, up-to-date accounting records.
        </p>
      </Card>

      {/* Prerequisites */}
      <Card className="p-6 border-amber-500/20 bg-amber-500/[0.02] animate-fade-in" data-testid="datev-prerequisites">
        <h2 className="text-lg font-heading font-bold mb-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" /> Prerequisites
        </h2>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" /> Active DATEV subscription with API access enabled</li>
          <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" /> DATEV Developer Portal account (developer.datev.de)</li>
          <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" /> API access scopes: Accounting Data Read, Invoice Read</li>
          <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" /> Veridex admin privileges</li>
        </ul>
      </Card>

      {/* Steps */}
      <section data-testid="datev-steps">
        <h2 className="text-lg font-heading font-bold mb-4">Setup Steps</h2>
        <div className="space-y-4">
          {steps.map((step) => (
            <Card key={step.num} className="p-5 animate-fade-in" data-testid={`datev-step-${step.num}`}>
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-heading font-bold text-primary">{step.num}</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-sm mb-1">{step.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{step.desc}</p>
                  {step.link && (
                    step.linkInternal ? (
                      <Link to={step.link} className="inline-flex items-center gap-1 mt-2 text-xs text-accent hover:underline">
                        <ArrowLeft className="w-3 h-3" /> Go to DATEV Settings
                      </Link>
                    ) : (
                      <a href={step.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs text-accent hover:underline">
                        <ExternalLink className="w-3 h-3" /> {step.link}
                      </a>
                    )
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Security */}
      <Card className="p-6 border-primary/20 bg-primary/[0.02] animate-fade-in" data-testid="datev-security">
        <h2 className="text-lg font-heading font-bold mb-3 flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" /> Security & Privacy
        </h2>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li className="flex items-start gap-2"><Key className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" /> Client Secret is encrypted at rest using AES-256 encryption</li>
          <li className="flex items-start gap-2"><Shield className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" /> Credentials are never logged or exposed in API responses</li>
          <li className="flex items-start gap-2"><Shield className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" /> All API communication uses TLS/HTTPS</li>
          <li className="flex items-start gap-2"><Shield className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" /> You can disconnect and remove credentials at any time</li>
        </ul>
      </Card>

      {/* FAQ */}
      <section data-testid="datev-faq">
        <h2 className="text-lg font-heading font-bold mb-4">Frequently Asked Questions</h2>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <Card key={i} className="p-5 animate-fade-in" data-testid={`datev-faq-${i}`}>
              <h3 className="font-heading font-semibold text-sm mb-1.5">{faq.q}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{faq.a}</p>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
